import { AppName, DevicePlatform } from '@zonofi/common/types/enum';

export enum ScreenOrientationType {
  LANDSCAPE = 'landscape-primary',
  PORTRAIT = 'portrait-primary',
}

export interface ValidateGstInResponse {
  firm_name: string;
  address: UserAddress;
  valid_till: string;
  gstin_number: string;
}

interface UserAddress {
  bnm: string;
  st: string;
  loc: string;
  bno: string;
  dst: string;
  lt: string;
  locality: string;
  pncd: string;
  landMark: string;
  stcd: string;
  geocodelvl: string;
  flno: string;
  lg: string;
}

export interface ValidateGstInRequest {
  gstNumber: string;
  sellerWorkspaceId: string;
  skipWorkspaceCheck?: boolean;
  customerId: string;
}

export interface DocumentResponse {
  url: string;
  sequenceNo: string;
  subType: string;
}

export interface DocumentRequest {
  workspaceId: string;
  documentType: string;
  entityId: string;
  mimeType?: string;
  subType: string;
  sequenceNo?: string;
}

export interface RecordEngagementRequest {
  sellerWorkspaceId: string;
  workspaceId: string;
  customerId: string;
  campaignId: string;
}

export interface RecordEngagementResponse {
  message: string;
}

export interface Workspace {
  id: string;
  teamOwnerInviteId: string;
  spaceName: string;
  code: string;
  location: null;
  address: null;
  userId: string;
  profilePic: string;
  timeZone: string;
  isBuyer: null;
  isSeller: boolean;
  businessCategory: null;
  industry: null;
  defaultCurrencyCode: string;
  isoLanguageCode: string;
  isoCountryCode: string;
  businessDetails: BusinessDetails;
  role: string;
  isSelfService: boolean;
  isSellerTeamOwner: boolean;
  user: User;
  gstValidityEndDate: null | string;
  balance?: string;
}

interface User {
  firstName: string;
  lastName: string;
  mobile: string;
  city: null;
  role: null;
  otp: string;
  emailOTP: string;
  profilePic: null;
  contentType: string;
  address: null;
  email: string;
  isVerified: boolean;
  isProfileUpdate: boolean;
  token: string;
  refreshToken: string;
  integrationToken: null;
  subtitle: null;
  registeredOn: string;
  createdAt: string;
  updatedAt: string;
  id: string;
  userType: null;
  isBotUser: boolean;
  autoPopulateOTP: boolean;
  isEmailVerified: boolean;
}

export interface OTPResponseChannel {
  isEmailAvailable: boolean;
  isPhoneAvailable: boolean;
}

export interface OTPResponseMobile {
  resendOtpCount: number;
  lastResendAttempt: string;
  message: string;
  attemptsLeft: number;
  timeLeft: number;
  resendAttemptsLeft: number;
  uom: string;
  sendOtp: boolean;
  otp: number;
  status: string;
  communicationUp: boolean;
}

export interface OTPResponseEmail {
  resendOtpCount: number;
  lastResendAttempt: string;
  message: string;
  attemptsLeft: number;
  timeLeft: number;
  resendAttemptsLeft: number;
  uom: string;
  email?: string;
  userId?: string;
  sendOtp: boolean;
  otp: number;
  status: string;
  communicationUp: boolean;
  tempToken?: string;
}

export interface ResendOTPResponse {
  resendOtpCount: number;
  lastResendAttempt: string;
  uom: string;
  sendOtp: boolean;
  communicationUp: boolean;
  message?: string;
  timeLeft?: number;
  status: string;
  attemptsLeft: number;
  resendAttemptsLeft: number;
  loginAttemptsLeftMessage: string;
  otp: number;
}

export interface SendOTPResponse {
  mfaStatus: boolean;
  channel: OTPResponseChannel;
  mobile: OTPResponseMobile;
  email: OTPResponseEmail;
  resendOtpCount?: number;
  lastResendAttempt?: string;
  uom?: string;
  sendOtp?: boolean;
  communicationUp?: boolean;
  message?: string;
  timeLeft?: number;
  status?: string;
  attemptsLeft?: number;
  resendAttemptsLeft?: number;
  loginAttemptsLeftMessage?: string;
  otp: number;
  temptoken: string;
}

export interface CreateUserResponse {
  firstName: string;
  id: string;
  email?: string;
  updatedAt?: string;
  isProfileUpdate?: boolean;
  mobile?: string;
  workspaceRoles: any;
  role?: string;
}

export interface UserData {
  newUser?: boolean;
  teamInviteId?: string;
  mobile: string;
  firstName: string;
  lastName: string;
  email?: string;
  id: string;
  role: string;
  profilePic?: string;
  contentType?: string;
  systemFirstName?: string;
  systemLastName?: string;
  city?: string;
  subtitle?: string;
  registeredOn?: string;
  address?: string;
  userType: string;
  token: string;
  refreshToken: string;
  isVerified: boolean;
  integrationToken: string;
  alertMessage?: string;
  attemptsLeft?: number;
  resendAttemptsLeft?: number;
  loginAttemptsLeftmessage?: string;
  lastLoginFailedAttempt?: string;
  uom?: string;
  message?: string;
  timeLeft?: number;
  isEmailVerified?: boolean;
  isPrincipal?: boolean;
  workspaceId?: string;
  workspaceRoles?: string[];
  inviteUserType?: string;
  customerCFADivisions?: ICfaDivision[];
  inviteDetails?: InviteDetails;
  inviteId?: string;
  temptoken?: string;
  isOtpValid?: boolean;
  authChannel?: string;
  mfaAuthChannel?: string;
  userProfile?: string;
  loggedInOnBehalfData: {
    isLoggedInOnBehalf: boolean;
    supportUserToken: string;
    supportUserRefereshToken: string;
    workspaceId: string;
  };
  cfaIDs?: string[];
}

export interface UserPermissionsResponse {
  id: string;
  createdDate: string;
  updatedDate: string;
  title: string;
  slug: string;
  isActive: boolean;
  description: string;
  parentRoleId: string;
  permissions: userPermission[];
}
export interface UserPermissionsRequest {
  roleSlugs: string[];
}

export interface userPermission {
  id: string;
  createdDate: string;
  updatedDate: string;
  title: string;
  slug: string;
  isActive: boolean;
  description?: string;
  code: string;
}

interface InviteDetails {
  id: string;
  phone: string;
  phone2: string;
  inviteType: string;
  userType?: string;
  isActive: boolean;
  isArchive: boolean;
  senderUserId: string;
  invitedUserId: string;
  senderWorkspaceId: string;
  invitedWorkspaceId: string;
  status: string;
  createdDate: string;
  updatedDate: string;
  inviteDetails: InviteDetaills;
}

interface InviteDetaills {
  id: string;
  distributorCode: string;
  stateId: string;
  state: { stateCode: string };
  email?: string;
}

export interface ICfaDivision {
  id: string;
  cfaDivisionCode: string;
  state: string;
  name: string;
  divisions: IDivision[];
}

export interface IDivision {
  id: string;
  code: string;
  name: string;
  headDivisionId: string;
}

export interface UserTokens {
  token: string;
  refreshToken: string;
  id?: string;
  workspaceId?: string;
  workspaceRoles?: string[];
  cfaIDs?: string[];
  inviteUserType?: string;
}

interface BusinessDetails {
  id: string;
  companyName: string;
  referenceCode: null;
  companyEmail: string;
  gstin: string;
  businessPAN: null;
  mobile: string;
  physicalAddress: PhysicalAddress;
  gstPortalUserName: null;
  gstPortalPassword: null;
  legalName: string;
  workspaceId: string;
  createdDate: string;
  updatedDate: string;
  isSandBox: boolean;
  isAutoInvoiceComm: boolean;
  communicationHierarchy: null | string;
  routeHierarchy: null | string;
  companyRefCode: string;
}

interface PhysicalAddress {
  postcode: string;
  city: string;
  state: string;
  neighborhood: string;
  formatted: string;
  suburb: string;
  pinCode: string;
}

export type AuthChannel = 'mobile' | 'email';

export interface SendOTPRequest {
  mobile?: string;
  authChannel?: AuthChannel;
  email?: string;
  inviteId?: string;
  doCreateUser?: boolean;
  message?: string;
}
export interface ResendOTPRequest {
  mobile?: string;
  authChannel?: AuthChannel;
  email?: string;
  inviteId?: string;
}

export interface VerifyOTPRequest {
  mobile?: string;
  authChannel?: AuthChannel;
  email?: string;
  otp: string;
  mfaStatus: boolean;
  fcmEnabled?: boolean;
}

export interface FCMTokenRequest {
  appName: AppName;
  deviceId?: string;
  token: string;
  userId: string;
  sellerWorkspaceId?: string;
  devicePlatform: DevicePlatform;
}
export interface FCMTokenResponse {
  appName: string;
  deviceId: string;
  token: string;
  inviteId: string;
  workspaceId: string;
  devicePlatform: string;
  lastUpdatedTime: string;
  id: string;
  createdDate: string;
  updatedDate: string;
  statusCode: number;
  responseStatus: string;
}

export interface OrgsDocumentResponse {
  subType: string;
  url: string;
}

export interface OrgsDocumentRequest {
  sellerWorkspaceId: string;
  documentType: string;
  entityId: string;
  mimeType?: string;
}
